import { useEffect, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { styles } from "../styles";
import { capabilityOptions, rolesByOrgType, roleLabels } from "../utils/constants";
import { displayName, hasCapability, isAdminTier, isManagerTier } from "../utils/capabilities";
import {
  DEPARTMENT_KEYS,
  DEPARTMENT_LABELS,
  DEPARTMENT_DESCRIPTIONS,
  applyDepartmentPreset,
} from "../utils/departmentPresets";
import BackButton from "../components/BackButton";

// Flip to true to surface the Danger Zone turnover-wipe card at the
// bottom of this page. Justin uses it once per environment when handing
// a test org over to its real operator. Hidden by default so admins
// can't fat-finger a destructive action they didn't intend; flip to
// true, deploy, run, flip back, deploy.
const WIPE_BUTTON_ENABLED = false;

export default function ManageUsersPage({
  orgId,
  orgType,
  currentUid,
  onEditUserProfile,
  onBack,
  actions = null,
  dataCounts = null,
  orgName = "",
}) {
  // Roles offered depend on the org type (contractor vs facility).
  const roleOptions = rolesByOrgType[orgType] || rolesByOrgType.mechanical;
  const defaultRole = roleOptions[roleOptions.length - 1]; // lowest tier

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState("");
  const [createError, setCreateError] = useState("");
  const [passwordResetLink, setPasswordResetLink] = useState("");
  const [expandedCaps, setExpandedCaps] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [capError, setCapError] = useState("");
  const [roleMsg, setRoleMsg] = useState("");

  // Danger Zone — one-time turnover wipe of operational data. Two
  // confirmation gates: a checkbox AND a typed-org-name match. See
  // useOrgData.wipeOperationalData.
  const [showWipe, setShowWipe] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [wipeAck, setWipeAck] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [wipeMsg, setWipeMsg] = useState("");
  const wipePhrase = orgName ? `delete ${orgName}` : "delete everything";
  const wipeConfirmMatches =
    wipeConfirmText.trim().toLowerCase() === wipePhrase.toLowerCase();

  const handleWipe = async () => {
    if (!actions?.wipeOperationalData) {
      setWipeMsg("Wipe action not available.");
      return;
    }
    if (!wipeAck || !wipeConfirmMatches) return;
    setWiping(true);
    setWipeMsg("");
    try {
      await actions.wipeOperationalData();
      setWipeMsg(
        "Done. Operational data cleared. Service tree, tickets, users, and org config preserved."
      );
      setWipeConfirmText("");
      setWipeAck(false);
      setShowWipe(false);
    } catch (e) {
      console.error("wipe failed:", e);
      setWipeMsg(`Wipe failed: ${e.message || e}`);
    } finally {
      setWiping(false);
    }
  };

  const handleSetRole = async (uid, newRole) => {
    setRoleMsg("");
    try {
      const setUserRole = httpsCallable(functions, "setUserRole");
      const result = await setUserRole({ uid, role: newRole });
      setRoleMsg(result.data?.message || "Role updated.");
    } catch (err) {
      console.error("Failed to change role", err);
      setRoleMsg(`Failed to change role: ${err.message}`);
    }
  };

  const handleToggleDisabled = async (u) => {
    setRoleMsg("");
    try {
      const setUserDisabled = httpsCallable(functions, "setUserDisabled");
      const result = await setUserDisabled({
        uid: u.id,
        disabled: !u.disabled,
      });
      setRoleMsg(result.data?.message || "Account updated.");
    } catch (err) {
      console.error("Failed to toggle account", err);
      setRoleMsg(`Failed to update account: ${err.message}`);
    }
  };

  const handleDeleteUser = async (u) => {
    const confirmed = window.confirm(
      `Delete ${u.email}?\n\nTheir login is disabled immediately and the account is restorable for 30 days, after which the login is permanently removed. All records they created (and their employee info) are kept either way.`
    );
    if (!confirmed) return;
    setRoleMsg("");
    try {
      const deleteOrgUser = httpsCallable(functions, "deleteOrgUser");
      const result = await deleteOrgUser({ uid: u.id });
      setRoleMsg(result.data?.message || "Account deleted.");
    } catch (err) {
      console.error("Failed to delete user", err);
      setRoleMsg(`Failed to delete user: ${err.message}`);
    }
  };

  const handleRestoreUser = async (u) => {
    setRoleMsg("");
    try {
      const restoreOrgUser = httpsCallable(functions, "restoreOrgUser");
      const result = await restoreOrgUser({ uid: u.id });
      setRoleMsg(result.data?.message || "Account restored.");
    } catch (err) {
      console.error("Failed to restore user", err);
      setRoleMsg(`Failed to restore: ${err.message}`);
    }
  };

  const daysUntilPurge = (deletedAt) => {
    if (!deletedAt || !deletedAt.toDate) return 30;
    const elapsedDays =
      (Date.now() - deletedAt.toDate().getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(30 - elapsedDays));
  };

  const [resetLinks, setResetLinks] = useState({});

  const handleResetPassword = async (u) => {
    setRoleMsg("");
    try {
      const getPasswordResetLink = httpsCallable(functions, "getPasswordResetLink");
      const result = await getPasswordResetLink({ uid: u.id });
      setResetLinks((prev) => ({
        ...prev,
        [u.id]: result.data?.passwordResetLink || "",
      }));
    } catch (err) {
      console.error("Failed to generate reset link", err);
      setRoleMsg(`Failed to generate reset link: ${err.message}`);
    }
  };

  const handleChangeEmail = async (u) => {
    const newEmail = window.prompt(
      `Change login email for ${u.email}?\n\nThe user will sign in with the new address next time. Their profile and history are kept.\n\nNew email:`,
      u.email || ""
    );
    if (!newEmail || newEmail === u.email) return;
    setRoleMsg("");
    try {
      const setUserEmail = httpsCallable(functions, "setUserEmail");
      const result = await setUserEmail({ uid: u.id, newEmail });
      setRoleMsg(result.data?.message || "Email updated.");
    } catch (err) {
      console.error("Failed to change email", err);
      setRoleMsg(`Failed to change email: ${err.message}`);
    }
  };

  const activeUsers = users.filter((u) => !u.deleted);
  const deletedUsers = users.filter((u) => u.deleted);

  useEffect(() => {
    if (!orgId) {
      setUsersLoading(false);
      return;
    }

    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setUsers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setUsersLoading(false);
      },
      (error) => {
        console.error("Error listing users", error);
        setUsersError(error.message);
        setUsersLoading(false);
      }
    );
    return unsub;
  }, [orgId]);

  const toggleCapability = async (u, capKey) => {
    const current = u.capabilities || {};
    // Flip the EFFECTIVE state (which respects role defaults) and store
    // the explicit boolean.
    const updated = {
      ...current,
      [capKey]: !hasCapability(u.role, current, capKey),
    };
    try {
      setCapError("");
      await updateDoc(doc(db, `users/${u.id}`), { capabilities: updated });
    } catch (err) {
      console.error("Failed to update capabilities", err);
      setCapError(
        `Could not save permission change: ${err.message}. (If this says "insufficient permissions", deploy the latest firestore rules.)`
      );
    }
  };

  // Assign a department + apply its capability preset. Active transition
  // per Justin (2026-06-15): confirm prompt → on confirm, the new preset
  // is merged into the user's capabilities AND the permissions panel
  // stays open so the admin can tick/untick anything else before walking
  // away. See [[project-departments-as-capability-presets]].
  const setDepartment = async (u, deptKey) => {
    if (!deptKey || deptKey === (u.department || "")) return;
    const label = DEPARTMENT_LABELS[deptKey] || deptKey;
    const ok = window.confirm(
      `Switch ${displayName(u, u.email)} to the "${label}" department?\n\n` +
        `This will apply that department's capability preset. The permissions ` +
        `panel will stay open so you can tweak anything before walking away.`
    );
    if (!ok) return;
    const nextCaps = applyDepartmentPreset(u.capabilities || {}, deptKey);
    try {
      setCapError("");
      await updateDoc(doc(db, `users/${u.id}`), {
        department: deptKey,
        capabilities: nextCaps,
      });
      // Keep capabilities panel open so admin sees the merged result and
      // can tune. setExpandedCaps is the parent state that drives the panel.
      setExpandedCaps((prev) => ({ ...prev, [u.id]: true }));
    } catch (err) {
      console.error("Failed to set department", err);
      setCapError(
        `Could not save department change: ${err.message}.`
      );
    }
  };

  const setHourlyRate = async (u, value) => {
    try {
      setCapError("");
      const trimmed = (value || "").trim();
      const num = trimmed === "" ? null : Number(trimmed);
      if (num !== null && (Number.isNaN(num) || num < 0)) {
        setCapError("Hourly rate must be a positive number.");
        return;
      }
      await updateDoc(doc(db, `users/${u.id}`), { hourlyRate: num });
    } catch (err) {
      console.error("Failed to update hourly rate", err);
      setCapError(`Could not save hourly rate: ${err.message}`);
    }
  };

  // Field-eligible flag — whether this user shows up in job-assignment
  // pickers and contract default-tech pickers. Defaults to TRUE for
  // existing users (undefined === true downstream) so behavior doesn't
  // change for current setups. Admin can toggle off for office staff
  // who'd otherwise clutter the picker (per Justin: "office ladies will
  // never go in the field but the president or service managers might").
  const setFieldEligible = async (u, value) => {
    try {
      setCapError("");
      await updateDoc(doc(db, `users/${u.id}`), { fieldEligible: !!value });
    } catch (err) {
      console.error("Failed to update field-eligible flag", err);
      setCapError(`Could not save field-eligibility: ${err.message}`);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateMessage("");
    setCreateError("");
    setPasswordResetLink("");

    try {
      const createUserForOrg = httpsCallable(functions, "createUserForOrg");
      // firstName + lastName are required server-side too; sending trimmed
      // values keeps the user-doc profile clean from the start. The new
      // employee's name shows on the app banner + signed work orders +
      // payroll records from day one, no extra Profile setup needed.
      const result = await createUserForOrg({
        email: email.trim(),
        role,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      setCreateMessage(result.data?.message || "User created.");
      setPasswordResetLink(result.data?.passwordResetLink || "");
      setFirstName("");
      setLastName("");
      setEmail("");
      setRole(defaultRole);
    } catch (err) {
      setCreateError(err.message || "Failed to create user.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />

      <h2>Manage Employees</h2>
      <p style={{ color: "#666" }}>
        Invite team members into your organization. They will receive a
        password-reset link to set their own password the first time.
      </p>

      <h3>Invite New User</h3>
      <div style={styles.card}>
        <input
          style={styles.input}
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <input
          style={styles.input}
          type="text"
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <input
          style={styles.input}
          type="email"
          placeholder="Email (used to sign in)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <br />
        <label>Role: </label>
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          {roleOptions.map((r) => (
            <option key={r} value={r}>
              {roleLabels[r] || r}
            </option>
          ))}
        </select>
        <br />
        <button
          style={styles.button}
          onClick={handleCreate}
          disabled={
            creating || !firstName.trim() || !lastName.trim() || !email.trim()
          }
        >
          {creating ? "Creating..." : "Invite User"}
        </button>

        {createMessage && (
          <p style={{ color: "green", marginTop: "8px" }}>{createMessage}</p>
        )}
        {createError && (
          <p style={{ color: "red", marginTop: "8px" }}>{createError}</p>
        )}

        {passwordResetLink && (
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              background: "#f0f8ff",
              border: "1px solid #4a90e2",
              borderRadius: "6px",
            }}
          >
            <strong>Password Reset Link (share with new user):</strong>
            <br />
            <input
              style={{ ...styles.input, fontSize: "11px", maxWidth: "100%" }}
              readOnly
              value={passwordResetLink}
              onFocus={(e) => e.target.select()}
            />
            <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0 0" }}>
              Copy this link and send it to the invitee. When they click it,
              they will set their password and can then log in.
            </p>
          </div>
        )}
      </div>

      <h3>Your Team</h3>
      {usersError && (
        <p style={{ color: "red" }}>Error loading users: {usersError}</p>
      )}
      {capError && <p style={{ color: "red" }}>{capError}</p>}
      {roleMsg && <p style={{ color: "#333" }}>{roleMsg}</p>}
      {usersLoading ? (
        <p>Loading...</p>
      ) : activeUsers.length === 0 ? (
        <p>No active users.</p>
      ) : (
        activeUsers
          .slice()
          .sort((a, b) => (a.email || "").localeCompare(b.email || ""))
          .map((u) => (
            <div key={u.id} style={styles.card}>
              <div
                onClick={() =>
                  setExpandedRows((prev) => ({
                    ...prev,
                    [u.id]: !prev[u.id],
                  }))
                }
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontFamily: "monospace",
                    color: "#666",
                    width: "12px",
                  }}
                >
                  {expandedRows[u.id] ? "▼" : "▶"}
                </span>
                <strong>{displayName(u.profile, u.email)}</strong>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    textTransform: "capitalize",
                  }}
                >
                  {(roleLabels[u.role] || u.role || "").replace(/_/g, " ")}
                </span>
                {u.id === currentUid && (
                  <span style={{ fontSize: "11px", color: "#1a5fb4" }}>
                    (you)
                  </span>
                )}
                {u.disabled && (
                  <span
                    style={{
                      color: "red",
                      fontWeight: "bold",
                      fontSize: "11px",
                    }}
                  >
                    DISABLED
                  </span>
                )}
              </div>

              {expandedRows[u.id] && (
              <>
              <div style={{ marginTop: "6px", fontSize: "12px", color: "#666" }}>
                {u.email}
              </div>
              {onEditUserProfile && (
                <div style={{ marginTop: "6px" }}>
                  <button
                    style={{ ...styles.button, margin: 0, fontSize: "12px" }}
                    onClick={() => onEditUserProfile(u)}
                  >
                    ✎ Edit Profile
                  </button>
                </div>
              )}
              <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                Role:{" "}
                {u.id === currentUid ? (
                  <strong>{u.role} (you)</strong>
                ) : (
                  <select
                    value={u.role}
                    onChange={(e) => handleSetRole(u.id, e.target.value)}
                  >
                    {roleOptions.map((r) => (
                      <option key={r} value={r}>
                        {roleLabels[r] || r}
                      </option>
                    ))}
                  </select>
                )}
                {(u.createdByName || u.createdBy) && ` • Added by: ${u.createdByName || u.createdBy}`}
                {u.createdAt &&
                  ` • Joined: ${u.createdAt.toDate().toLocaleDateString()}`}
              </div>

              <div style={{ marginTop: "8px", fontSize: "12px" }}>
                <label>
                  Hourly Rate ($):{" "}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={u.hourlyRate ?? ""}
                    placeholder="optional"
                    onBlur={(e) => {
                      if (
                        String(e.target.value) !== String(u.hourlyRate ?? "")
                      ) {
                        setHourlyRate(u, e.target.value);
                      }
                    }}
                    style={{
                      width: "100px",
                      padding: "4px 6px",
                      fontSize: "12px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </label>
                <span style={{ color: "#666", marginLeft: "8px" }}>
                  Used for payroll labor cost. Leave empty to show hours only.
                </span>
              </div>

              <div style={{ marginTop: "8px", fontSize: "12px" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={u.fieldEligible !== false}
                    onChange={(e) => setFieldEligible(u, e.target.checked)}
                  />{" "}
                  Field-eligible (shows up in job-assignment + contract
                  default-tech pickers)
                </label>
                <div style={{ color: "#666", marginTop: "2px" }}>
                  Uncheck for office staff who don't go in the field — keeps
                  the picker focused on people who actually run service calls.
                  Default ON for everyone unless toggled off.
                </div>
              </div>

              {isAdminTier(u.role) ? (
                <div
                  style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
                >
                  Full access ({roleLabels[u.role] || u.role})
                </div>
              ) : (
                <div style={{ marginTop: "8px" }}>
                  <button
                    style={styles.button}
                    onClick={() =>
                      setExpandedCaps((prev) => ({
                        ...prev,
                        [u.id]: !prev[u.id],
                      }))
                    }
                  >
                    {expandedCaps[u.id]
                      ? "Hide Capabilities ▴"
                      : "Capabilities ▾"}
                  </button>

                  {expandedCaps[u.id] && (
                    <div
                      style={{
                        marginTop: "6px",
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                      }}
                    >
                      {/* Department dropdown — preset-applies a capability
                          bundle to the per-checkbox grid below. Admin tier
                          (admin / executive) is unrestricted by role
                          already; department concept doesn't apply since
                          hasCapability returns true regardless of stored
                          caps. So we suppress the picker entirely for them
                          and show a plain note instead. */}
                      {isAdminTier(u.role) ? (
                        <div
                          style={{
                            marginBottom: "10px",
                            paddingBottom: "8px",
                            borderBottom: "1px dashed #ddd",
                            fontSize: "12px",
                            color: "#1a5f1f",
                            background: "#d4edda",
                            padding: "8px 10px",
                            borderRadius: "4px",
                          }}
                        >
                          <strong>Admin role — unrestricted access.</strong>{" "}
                          Departments + capability toggles only apply to
                          non-admin users.
                        </div>
                      ) : (
                        <div
                          style={{
                            marginBottom: "10px",
                            paddingBottom: "8px",
                            borderBottom: "1px dashed #ddd",
                          }}
                        >
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              marginRight: "6px",
                            }}
                          >
                            Department:
                          </label>
                          <select
                            value={u.department || ""}
                            onChange={(e) => setDepartment(u, e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {DEPARTMENT_KEYS.map((k) => (
                              <option key={k} value={k}>
                                {DEPARTMENT_LABELS[k]}
                              </option>
                            ))}
                          </select>
                          {u.department && DEPARTMENT_DESCRIPTIONS[u.department] && (
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#666",
                                margin: "4px 0 0 0",
                              }}
                            >
                              {DEPARTMENT_DESCRIPTIONS[u.department]}
                            </p>
                          )}
                        </div>
                      )}
                      <p
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          margin: "0 0 6px 0",
                        }}
                      >
                        {isManagerTier(u.role)
                          ? "Managers have everything by default — uncheck to restrict."
                          : "Technicians have nothing extra by default — check to grant."}
                      </p>
                      {capabilityOptions
                        .filter((cap) => !cap.orgType || cap.orgType === orgType)
                        .map((cap) => (
                        <label
                          key={cap.key}
                          style={{ display: "block", fontSize: "12px" }}
                        >
                          <input
                            type="checkbox"
                            checked={hasCapability(
                              u.role,
                              u.capabilities,
                              cap.key
                            )}
                            onChange={() => toggleCapability(u, cap.key)}
                          />{" "}
                          {cap.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: "10px" }}>
                <button
                  style={styles.button}
                  onClick={() => handleResetPassword(u)}
                >
                  Send Password Reset
                </button>
                <button
                  style={styles.button}
                  onClick={() => handleChangeEmail(u)}
                >
                  Change Email
                </button>
              </div>
              {resetLinks[u.id] && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px",
                    background: "#f0f8ff",
                    border: "1px solid #4a90e2",
                    borderRadius: "6px",
                  }}
                >
                  <strong style={{ fontSize: "12px" }}>
                    Password reset link (send to {u.email}):
                  </strong>
                  <input
                    style={{ ...styles.input, fontSize: "11px", maxWidth: "100%" }}
                    readOnly
                    value={resetLinks[u.id]}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              )}

              {u.id !== currentUid && (
                <div style={{ marginTop: "10px" }}>
                  <button
                    style={styles.button}
                    onClick={() => handleToggleDisabled(u)}
                  >
                    {u.disabled ? "Enable Account" : "Disable Account"}
                  </button>
                  <button
                    style={{ ...styles.button, color: "red" }}
                    onClick={() => handleDeleteUser(u)}
                  >
                    Delete Account
                  </button>
                </div>
              )}
              </>
              )}
            </div>
          ))
      )}

      {WIPE_BUTTON_ENABLED && actions?.wipeOperationalData && (
        <div
          style={{
            ...styles.card,
            background: "#fff5f5",
            border: "2px solid #c44",
            marginTop: "20px",
          }}
        >
          <h3 style={{ color: "#7a1f1f", marginTop: 0 }}>
            Danger Zone — Turnover Wipe
          </h3>
          <p style={{ fontSize: "13px", color: "#374151" }}>
            One-time clean slate before handing this org to a real user.
            Deletes <strong>all contracts, jobs (with their bills/invoices),
            quotes, purchase orders, and vendors</strong> for this
            contractor + resets the YYYY-#### counters. Public mirrors
            (publicJobs / publicInvoices / publicQuotes / publicContracts)
            scoped to this contractor get wiped too so the facility side
            doesn't show ghosts.
          </p>
          <p style={{ fontSize: "13px", color: "#374151" }}>
            <strong>Preserved:</strong> service tree (customers, locations,
            equipment), service tickets, user accounts, org config (logo,
            quote defaults, billing defaults, payroll config).
          </p>
          {dataCounts && (
            <div
              style={{
                fontSize: "12px",
                color: "#7a1f1f",
                background: "#fff",
                border: "1px solid #ecc",
                borderRadius: "4px",
                padding: "8px 10px",
                marginBottom: "10px",
              }}
            >
              <strong>Will delete now:</strong>{" "}
              {dataCounts.contracts ?? 0} contracts ·{" "}
              {dataCounts.jobs ?? 0} jobs ·{" "}
              {dataCounts.quotes ?? 0} quotes ·{" "}
              {dataCounts.purchaseOrders ?? 0} POs ·{" "}
              {dataCounts.vendors ?? 0} vendors
            </div>
          )}
          {!showWipe ? (
            <button
              style={{
                ...styles.button,
                background: "#c44",
                color: "white",
              }}
              onClick={() => {
                setShowWipe(true);
                setWipeMsg("");
              }}
            >
              Show wipe controls…
            </button>
          ) : (
            <>
              <label
                style={{ display: "block", fontSize: "13px", marginTop: "6px" }}
              >
                <input
                  type="checkbox"
                  checked={wipeAck}
                  onChange={(e) => setWipeAck(e.target.checked)}
                />{" "}
                I understand this is permanent and irreversible.
              </label>
              <div style={{ marginTop: "8px" }}>
                <label style={{ fontSize: "13px", display: "block" }}>
                  Type <code>{wipePhrase}</code> to confirm:
                </label>
                <input
                  type="text"
                  value={wipeConfirmText}
                  onChange={(e) => setWipeConfirmText(e.target.value)}
                  placeholder={wipePhrase}
                  style={{
                    ...styles.input,
                    margin: "4px 0 0",
                    width: "100%",
                    maxWidth: "320px",
                  }}
                  disabled={wiping}
                />
              </div>
              <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                <button
                  style={{
                    ...styles.button,
                    background: "#c44",
                    color: "white",
                    opacity: wipeAck && wipeConfirmMatches && !wiping ? 1 : 0.5,
                  }}
                  disabled={!wipeAck || !wipeConfirmMatches || wiping}
                  onClick={handleWipe}
                >
                  {wiping ? "Wiping…" : "Wipe operational data"}
                </button>
                <button
                  style={styles.button}
                  disabled={wiping}
                  onClick={() => {
                    setShowWipe(false);
                    setWipeConfirmText("");
                    setWipeAck(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
          {wipeMsg && (
            <div
              style={{
                marginTop: "10px",
                fontSize: "13px",
                color: wipeMsg.startsWith("Done") ? "#1a5f1f" : "#7a1f1f",
              }}
            >
              {wipeMsg}
            </div>
          )}
        </div>
      )}

      {deletedUsers.length > 0 && (
        <>
          <h3>Deleted Accounts</h3>
          <p style={{ fontSize: "12px", color: "#666" }}>
            Logins are disabled. Each is restorable until its login is
            permanently removed ~30 days after deletion. All records and
            employee info are kept regardless.
          </p>
          {deletedUsers
            .slice()
            .sort((a, b) => (a.email || "").localeCompare(b.email || ""))
            .map((u) => (
              <div key={u.id} style={{ ...styles.card, background: "#ffecec" }}>
                <strong>{displayName(u.profile, u.email)}</strong>{" "}
                <span style={{ fontSize: "12px", color: "#666" }}>{u.email}</span>{" "}
                — {u.role}
                <div style={{ fontSize: "12px", color: "#666" }}>
                  Deleted{u.deletedBy ? ` by ${u.deletedBy}` : ""}
                  {u.deletedAt &&
                    u.deletedAt.toDate &&
                    ` on ${u.deletedAt.toDate().toLocaleDateString()}`}
                  {u.purged
                    ? " • Login permanently removed (record kept)"
                    : ` • ${daysUntilPurge(u.deletedAt)} day(s) until permanent removal`}
                </div>
                {!u.purged && (
                  <button
                    style={{ ...styles.button, marginTop: "8px" }}
                    onClick={() => handleRestoreUser(u)}
                  >
                    Restore Account
                  </button>
                )}
              </div>
            ))}
        </>
      )}
    </>
  );
}
